import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { UnregisterCallback } from 'history';
import { useRouter } from './RouteProvider';

type UseBlockNavigationParams = {
  /** Whether navigation should be blocked (e.g. form has unsaved changes) */
  shouldBlock: boolean;
};

type UseBlockNavigationReturn = {
  /** Whether the navigation block prompt should be shown */
  isNavigationBlocked: boolean;
  /**
   * Call when the user confirms they want to leave.
   */
  handleContinueNav: () => void | Promise<void>;
  /**
   * Call when the user cancels the prompt.
   */
  handleCancelNav: () => void | Promise<void>;
};

/**
 * Hook that blocks navigation when `when` is true and exposes state/callbacks
 * for rendering a confirmation prompt (e.g. UnsavedChangesGuardPrompt).
 *
 * Uses `history.block()` internally (v5-only — will be replaced in a future phase).
 *
 * @example
 * ```tsx
 * import { useBlockNavigation } from '@blocs.router';
 * import UnsavedChangesGuardPrompt from '@minecraft.unsaved-changes-guard';
 *
 * const { isNavigationBlocked, handleContinueNav, handleCancelNav } = useBlockNavigation({
 *   shouldBlock: formIsDirty,
 * });
 *
 * return (
 *   <>
 *     {isNavigationBlocked && (
 *       <UnsavedChangesGuardPrompt
 *         promptText="You have unsaved changes. Are you sure you want to leave?"
 *         handleContinueNav={handleContinueNav}
 *         handleCancelNav={handleCancelNav}
 *       />
 *     )}
 *   </>
 * );
 * ```
 */
export const useBlockNavigation = ({ shouldBlock }: UseBlockNavigationParams): UseBlockNavigationReturn => {
  const history = useHistory();
  const { navigate } = useRouter();

  const [showPrompt, setShowPrompt] = useState(false);
  const [currentPath, setCurrentPath] = useState('');

  const unblockRef = useRef<UnregisterCallback>();

  useEffect(() => {
    unblockRef.current = history.block((prompt) => {
      if (shouldBlock) {
        setCurrentPath(prompt.pathname);
        setShowPrompt(true);

        return false;
      }

      return undefined;
    });

    return (): void => {
      unblockRef?.current();
    };
  }, [history, shouldBlock]);

  const handleContinueNav = useCallback(async () => {
    unblockRef?.current();
    navigate({ to: currentPath });
  }, [currentPath, navigate]);

  const handleCancelNav = useCallback(async () => {
    setShowPrompt(false);
  }, []);

  return useMemo(
    () => ({
      isNavigationBlocked: showPrompt,
      handleContinueNav,
      handleCancelNav,
    }),
    [showPrompt, handleContinueNav, handleCancelNav]
  );
};
